import React from 'react';
import { NextPage } from 'next';
import router from 'next/router';
import { batch } from 'react-redux';
import ConfigurableTrimPage from '@components/TrimPage/Configurable';
import { FeatureSwitchesContext } from '../../../types/featureSwitches';
import { ContextWithStore } from '@utils/getInitialProps/@types';
import { filterOutCashOnlyVehicles } from '@utils/FinanceWidget.utils';
import { handleFilters, updateGeneralContext } from '@utils/getInitialProps/common';
import { HandleFiltersResponse } from '@utils/getInitialProps/@types';
import { handleInitialURLParams } from '@utils/page.utils';
import { handleSorting } from '@utils/getInitialProps/common';
import { sortOffersDescByPriority } from '@utils/Offers.utils';
import FinanceWidgetDuck from '../../../redux/financeWidget/financeWidget.duck';
import {
    CarJourneyType,
    getCarTrimConfigurations,
    getCarTrims,
    ICarTrimConfigurationsResponse,
    ICarTrimsResponse,
    TBudgetType,
} from '../../../services';
import { FEATURES_LIST, IFeatureSwitchContext } from '../../../context/featureSwitchApp';
import { CASH, FINANCE } from '../../../services/filters/filters';
import dynamic from 'next/dynamic';
import { PAGE_VERSIONS } from '../../../constants/main';
import CarDetailsDuck from 'src/redux/carDetails/carDetails.duck';
import FilterDuck from 'src/redux/filters/filter.duck';
import GlobalDuck from 'src/redux/global/global.duck';
import TrimSelectorDuck from 'src/redux/trimSelector/trimSelector.duck';
import UIDuck from 'src/redux/commonDucks/ui.duck';

export interface ISelectorPageProps {
    nameplateBodyStyleSlug?: string;
    featureContext?: IFeatureSwitchContext;
}

const trimPageVersionComponents: Record<
    IFeatureSwitchContext['FEATURE_SWITCH_CONFIGURABLE_TRIM_PAGE_VERSION'],
    React.ComponentType<ISelectorPageProps>
> = {
    v1: dynamic(() => import('@components/TrimPage/Configurable').then((mod) => mod)),
    v2: dynamic(() => import('@components/v2/ConfigurableTrim').then((mod) => mod.ConfigurableTrim)),
};

const TrimPage: NextPage<ISelectorPageProps> = (props) => {
    const Component =
        trimPageVersionComponents[props.featureContext?.FEATURE_SWITCH_CONFIGURABLE_TRIM_PAGE_VERSION ?? 'v1'];
    return <Component {...props} />;
};

TrimPage.getInitialProps = async (ctx: FeatureSwitchesContext<ContextWithStore>): Promise<any> => {
    const { store, query, asPath, res, featureContext } = await updateGeneralContext(ctx, {
        headers: [
            {
                key: 'Cache-Control',
                value: `private, no-cache`,
            },
        ],
    });
    const { model } = query;
    const nameplateBodyStyleSlug = model[0];
    const speckPackSlug = model[1];

    const defaultPaymentJourney: TBudgetType = featureContext[FEATURES_LIST.FEATURE_SWITCH_DEFAULT_PAYMENT_JOURNEY];
    const isCashDisabled = featureContext[FEATURES_LIST.FEATURE_SWITCH_IS_CASH_DISABLED];
    const isFinanceDisabled = featureContext[FEATURES_LIST.FEATURE_SWITCH_IS_FINANCE_DISABLED];
    const trimPageVersion = featureContext[FEATURES_LIST.FEATURE_SWITCH_CONFIGURABLE_TRIM_PAGE_VERSION];

    const DESERIALIZATION_ERROR = 'DeSerializationError';
    let paymentJourneyForDataRequests: TBudgetType = defaultPaymentJourney;

    if (isCashDisabled && !isFinanceDisabled) {
        paymentJourneyForDataRequests = FINANCE;
    } else if (!isCashDisabled) {
        paymentJourneyForDataRequests = CASH;
    }

    try {
        handleInitialURLParams(store, asPath, res, router, featureContext);

        const sorting = handleSorting(store, query);

        const previousAllTrims = TrimSelectorDuck.getAllConfigurableTrims(store.getState()).sort(
            sortOffersDescByPriority
        );
        const hasTrimsAndConfigurations =
            previousAllTrims?.length > 0 && previousAllTrims?.[0]?.nameplateBodyStyleSlug === nameplateBodyStyleSlug;

        let allTrims: ICarTrimsResponse = { statusCode: 200, items: previousAllTrims };
        let trimConfigurations: ICarTrimConfigurationsResponse;
        if (!hasTrimsAndConfigurations) {
            allTrims = await getCarTrims(
                defaultPaymentJourney as TBudgetType,
                nameplateBodyStyleSlug as string,
                [],
                sorting
            );
            trimConfigurations =
                trimPageVersion === PAGE_VERSIONS.V2
                    ? { statusCode: 200, items: [] }
                    : await getCarTrimConfigurations(
                          paymentJourneyForDataRequests as TBudgetType,
                          nameplateBodyStyleSlug as string,
                          [],
                          sorting
                      );
        }
        const filters: HandleFiltersResponse = await handleFilters(
            store,
            query,
            defaultPaymentJourney as TBudgetType,
            nameplateBodyStyleSlug as string
        );

        let filteredTrims: ICarTrimsResponse;
        if (filters?.exportedFiltersWithPrice?.length > 0) {
            filteredTrims = await getCarTrims(
                defaultPaymentJourney as TBudgetType,
                nameplateBodyStyleSlug as string,
                filters.exportedFiltersWithPrice,
                sorting
            );
        }

        batch(() => {
            store.dispatch(GlobalDuck.setCarJourney(CarJourneyType.CONFIGURABLE));
            if (!hasTrimsAndConfigurations) {
                store.dispatch(
                    TrimSelectorDuck.setTrimConfigurations(
                        paymentJourneyForDataRequests === FINANCE
                            ? filterOutCashOnlyVehicles(trimConfigurations.items)
                            : trimConfigurations.items
                    )
                );
                store.dispatch(TrimSelectorDuck.setAllTrims(allTrims.items));
            }

            if (filters?.exportedFiltersWithPrice?.length > 0 && !filteredTrims?.items?.length) {
                store.dispatch(UIDuck.setIsNoCarError(true));
            } else {
                store.dispatch(UIDuck.setIsNoCarError(false));
                if (filters?.exportedFiltersWithPrice?.length > 0) {
                    store.dispatch(TrimSelectorDuck.setFilteredTrims(filteredTrims?.items || []));
                } else {
                    store.dispatch(TrimSelectorDuck.setFilteredTrims(allTrims?.items || []));
                }
                store.dispatch(
                    FilterDuck.changeBudgetType(featureContext[FEATURES_LIST.FEATURE_SWITCH_DEFAULT_PAYMENT_JOURNEY])
                );
                store.dispatch(FinanceWidgetDuck.setFinanceQuote(null));
                // Select first (per offer priority) trim as carDetails - this will be the actively selected trim on Trim Page
                if (!speckPackSlug) {
                    store.dispatch(
                        CarDetailsDuck.setCurrentCarDetails(
                            (filteredTrims?.items ?? allTrims?.items)?.sort(sortOffersDescByPriority)[0]
                        )
                    );
                } else {
                    const carDetailsBySpeckPack = (filteredTrims?.items ?? allTrims?.items)?.find(
                        (carDetails) => carDetails?.specPackSlug === speckPackSlug
                    );

                    store.dispatch(
                        CarDetailsDuck.setCurrentCarDetails(
                            carDetailsBySpeckPack ||
                                (filteredTrims?.items ?? allTrims?.items)?.sort(sortOffersDescByPriority)[0]
                        )
                    );
                }
            }
        });
    } catch (error: any) {
        // @ts-ignore
        if (error?.name === DESERIALIZATION_ERROR) {
            store.dispatch(TrimSelectorDuck.setFilteredTrims([]));
        }
    }
    return { query, nameplateBodyStyleSlug, namespacesRequired: ['common'], featureContext };
};

export default TrimPage;
